import { c as _c } from "react/compiler-runtime";
import { LastInteractionTagFilterConfig, TimeRangeConfig } from '@/api/generated/model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAvailableTags } from '@/hooks/use-available-tags';
import { useProjectsContext } from '@/providers/projects-provider';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { Button } from '@/comps/primitives/button';
import { FILTERS, addTagPrefix, isTag, removeTagPrefix } from '@/models/common/filters';
import { FilterButton } from '@/comps/FilterPicker/FilterButton';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { filterToOperator, getOperatorLabel } from '@/comps/FilterPicker/filter-operators';
import { humanizeString } from '@/utils/string.utils';
type LastInteractionTagInitialValues = Pick<LastInteractionTagFilterConfig, 'tag' | 'values' | 'is_exclude' | 'select_all' | 'search'>;
type Props = {
  title?: string;
  timeRange?: TimeRangeConfig;
  initialValues?: LastInteractionTagInitialValues;
  onApply: (config: LastInteractionTagFilterConfig) => void;
};
function tagFilterFromInitial(initial?: LastInteractionTagInitialValues): GenericFilter[] {
  if (!initial?.tag) return [];
  return [{
    column: addTagPrefix(initial.tag),
    values: initial.values ?? [],
    is_exclude: initial.is_exclude,
    select_all: initial.select_all,
    search: initial.search
  }];
}
function toLastInteractionTagConfig(filter: GenericFilter | undefined): LastInteractionTagFilterConfig | null {
  if (!filter?.column || !isTag(filter.column)) return null;
  const tag = removeTagPrefix(filter.column);
  if (!tag) return null;
  const values = Array.isArray(filter.values) ? filter.values : [];
  const search = filter.search?.trim();
  if (!filter.select_all && values.length === 0 && !search) return null;
  return {
    column: FILTERS.lastInteractionTag,
    tag,
    values,
    is_exclude: filter.is_exclude,
    select_all: filter.select_all,
    search: search || undefined
  };
}
function buildTriggerLabel(filter: GenericFilter | undefined): string {
  if (!filter?.column || !isTag(filter.column)) return 'Select tag';
  const tagName = humanizeString(removeTagPrefix(filter.column));
  const values = Array.isArray(filter.values) ? filter.values.map(v => humanizeString(v)) : [];
  const valuesLabel = filter.select_all ? 'All' : filter.search ? filter.search : values.join(', ');
  return `${tagName} ${getOperatorLabel(filterToOperator(filter)).toLowerCase()}: ${valuesLabel}`;
}
export const LastInteractionTagForm = t0 => {
  const $ = _c(45);
  const {
    title: t1,
    timeRange: propTimeRange,
    initialValues,
    onApply
  } = t0;
  const title = t1 === undefined ? "whose last interaction has tag" : t1;
  const {
    selectedProject
  } = useProjectsContext();
  const {
    timeRange: globalTimeRange
  } = useGlobalFiltersContext();
  const timeRange = propTimeRange ?? globalTimeRange;
  const projectId = selectedProject?.id ?? "";
  let t2;
  if ($[0] !== initialValues) {
    t2 = () => tagFilterFromInitial(initialValues);
    $[0] = initialValues;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [filters, setFilters] = useState(t2);
  let t3;
  if ($[2] !== initialValues) {
    t3 = () => {
      setFilters(tagFilterFromInitial(initialValues));
    };
    $[2] = initialValues;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = initialValues?.tag;
  const t5 = initialValues?.values;
  const t6 = initialValues?.is_exclude;
  const t7 = initialValues?.select_all;
  const t8 = initialValues?.search;
  let t9;
  if ($[4] !== t4 || $[5] !== t5 || $[6] !== t6 || $[7] !== t7 || $[8] !== t8) {
    t9 = [t4, t5, t6, t7, t8];
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  useEffect(t3, t9);
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const {
    availableTags,
    isLoading: isLoadingTags
  } = useAvailableTags(projectId, t10, timeRange);
  availableTags?.tags;
  let t11;
  if ($[11] !== availableTags?.tags) {
    t11 = availableTags?.tags ?? [];
    $[11] = availableTags?.tags;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11) {
    t12 = t11.map(_temp);
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const availableFilterKinds = t12;
  let t13;
  if ($[15] !== filters) {
    t13 = filters.find(_temp2);
    $[15] = filters;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const selectedTagFilter = t13;
  let t14;
  if ($[17] !== selectedTagFilter) {
    t14 = buildTriggerLabel(selectedTagFilter);
    $[17] = selectedTagFilter;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const triggerLabel = t14;
  let t15;
  if ($[19] !== selectedTagFilter) {
    t15 = toLastInteractionTagConfig(selectedTagFilter);
    $[19] = selectedTagFilter;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const canApply = !!t15;
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = next => {
      const tagFilter = [...next].reverse().find(_temp3);
      setFilters(tagFilter ? [tagFilter] : []);
    };
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const handleSetFilters = t16;
  let t17;
  if ($[22] !== onApply || $[23] !== selectedTagFilter) {
    t17 = () => {
      const config = toLastInteractionTagConfig(selectedTagFilter);
      if (!config) {
        return;
      }
      onApply(config);
    };
    $[22] = onApply;
    $[23] = selectedTagFilter;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const apply = t17;
  let t18;
  if ($[25] !== title) {
    t18 = <p className="text-sm">{title}</p>;
    $[25] = title;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const t19 = !availableFilterKinds.length && !isLoadingTags;
  const t20 = triggerLabel !== "Select tag" ? triggerLabel : undefined;
  let t21;
  if ($[27] !== t19 || $[28] !== t20 || $[29] !== triggerLabel) {
    t21 = <Button type="button" variant="outline" className="max-w-full justify-start truncate" disabled={t19} title={t20}>{triggerLabel}</Button>;
    $[27] = t19;
    $[28] = t20;
    $[29] = triggerLabel;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== availableFilterKinds || $[32] !== filters || $[33] !== isLoadingTags || $[34] !== projectId || $[35] !== t21 || $[36] !== timeRange) {
    t22 = <FilterButton filters={filters} setFilters={handleSetFilters} timeRange={timeRange} projectId={projectId} volumeKind="users" availableFilterKinds={availableFilterKinds} loading={isLoadingTags} hideSavedFilters={true} align="left" side="bottom">{t21}</FilterButton>;
    $[31] = availableFilterKinds;
    $[32] = filters;
    $[33] = isLoadingTags;
    $[34] = projectId;
    $[35] = t21;
    $[36] = timeRange;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const t23 = !canApply;
  let t24;
  if ($[38] !== apply || $[39] !== t23) {
    t24 = <Button size="sm" onClick={apply} disabled={t23}>Apply</Button>;
    $[38] = apply;
    $[39] = t23;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t18 || $[42] !== t22 || $[43] !== t24) {
    t25 = <div className="flex flex-col gap-2">{t18}{t22}{t24}</div>;
    $[41] = t18;
    $[42] = t22;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  return t25;
};
function _temp(tag) {
  return addTagPrefix(tag);
}
function _temp2(f) {
  return isTag(f.column);
}
function _temp3(filter) {
  return isTag(filter.column);
}